import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useAuthContext } from '../../../auth/useAuthContext';
import moment from 'moment';
import { DispenseService } from '../../../api/dispense.service';
import { getDispenseTillDisplayInstant } from './tillDispenseDisplay';
import { formatLabel } from '../../../utils/formatLabel';

interface Props {
  typographyStyles?: CSSProperties;
  buttonStyles?: CSSProperties;
  tillApiData?: any;
  selectedDispenseTill?: any;
}

type OrderItem = {
  name: string;
  price: number;
  quantity: number;
  /** Dispense category of the item, e.g. "Prescription Frames". */
  category?: string;
};

/** Receipt money: always two decimals with thousands separators. */
function formatReceiptMoney(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return `£${n.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Collapse identical lines (same name, unit price and category) into one line
 * with a summed quantity, so six of the same frame read "6 x …" once.
 */
function groupOrderItems(items: OrderItem[]): OrderItem[] {
  const grouped: OrderItem[] = [];
  const indexByKey = new Map<string, number>();
  for (const item of items) {
    const key = `${item.name}|${item.price}|${item.category ?? ''}`;
    const at = indexByKey.get(key);
    if (at === undefined) {
      indexByKey.set(key, grouped.length);
      grouped.push({ ...item });
    } else {
      grouped[at] = {
        ...grouped[at],
        quantity: grouped[at].quantity + item.quantity,
      };
    }
  }
  return grouped;
}

/** Turn a stored product category/type slug into a readable receipt label. */
const formatCategoryLabel = (raw?: string | null): string => {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .trim()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

type SummaryItem = {
  label: string;
  value: number;
};

const OrderSummary = ({
  typographyStyles,
  buttonStyles,
  tillApiData,
  selectedDispenseTill,
}: Props) => {
  // Add print styles when component mounts
  useEffect(() => {
    const printStyles = `
      @media print {
        body * {
          visibility: hidden;
        }
        .print-receipt, .print-receipt * {
          visibility: visible;
        }
        .print-receipt {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          max-width: none !important;
          border: none !important;
          box-shadow: none !important;
          margin: 0 !important;
          padding: 20px !important;
        }
        .print-receipt .no-print {
          display: none !important;
        }
        .print-receipt h6 {
          font-size: 24px !important;
          margin-bottom: 20px !important;
          text-align: center;
        }
        .print-receipt .order-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }
        .print-receipt .summary-section {
          border-top: 2px solid #000;
          margin-top: 15px;
          padding-top: 10px;
        }
        .print-receipt .total-section {
          border-top: 3px double #000;
          margin-top: 15px;
          padding-top: 10px;
          font-weight: bold;
        }
        .print-receipt .dispense-info {
          text-align: center;
          margin-bottom: 15px;
          font-size: 12px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
        }
      }
    `;

    const styleSheet = document.createElement('style');
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const { user } = useAuthContext();

  const [dispenseRecord, setDispenseRecord] = useState<any | null>(null);

  useEffect(() => {
    let cancelled = false;
    const id = selectedDispenseTill?.dispenseId;
    if (id == null) {
      setDispenseRecord(null);
      return undefined;
    }
    (async () => {
      try {
        const res = await new DispenseService().getDispense(Number(id));
        const list = res.data?.dispenses ?? [];
        const match = list.find((d: any) => Number(d.id) === Number(id));
        if (!cancelled) {
          setDispenseRecord(match ?? null);
        }
      } catch {
        if (!cancelled) {
          setDispenseRecord(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedDispenseTill?.dispenseId]);

  const receiptRef = useMemo(() => {
    const notes = dispenseRecord?.orderingNotes;
    if (!notes || typeof notes !== 'string') {
      return '';
    }
    const m = notes.match(/Receipt:\s*([^\n]+)/);
    return m?.[1]?.trim() ?? '';
  }, [dispenseRecord]);

  const orderItems: OrderItem[] = useMemo(() => {
    if (!selectedDispenseTill) {
      return [{ name: 'No dispense till selected', price: 0, quantity: 0 }];
    }

    const fromProductLines = (lines: any[]): OrderItem[] =>
      (lines ?? []).map((line) => {
        const p = line.product ?? line;
        const lot = line.batchNumber ?? line.goodsInId;
        const base = p?.name ?? 'Product';
        return {
          name: lot ? `${base} (${lot})` : base,
          price: Number(p?.salePrice ?? p?.price ?? 0),
          quantity: line.quantity ?? 1,
          // Fall back to the product type when no category is set, so the line
          // still says what kind of item was dispensed.
          category: formatCategoryLabel(p?.category ?? p?.type),
        };
      });

    const fromAddons = (addons: any[]): OrderItem[] =>
      (addons ?? []).map((a) => ({
        name: a.name ? `Add-on: ${a.name}` : 'Add-on',
        price: Number(a.price ?? 0),
        quantity: 1,
        category: 'Add-ons & Coatings',
      }));

    const lineCandidates =
      (dispenseRecord?.productLines?.length ?? 0) > 0
        ? dispenseRecord.productLines
        : (dispenseRecord?.products ?? []).map((p: any) => ({
            product: p,
            quantity: 1,
          }));

    if (
      dispenseRecord &&
      (lineCandidates.length > 0 || (dispenseRecord.addons?.length ?? 0) > 0)
    ) {
      return groupOrderItems([
        ...fromProductLines(lineCandidates),
        ...fromAddons(dispenseRecord.addons),
      ]);
    }

    if (user?.roleId === 'patient' && selectedDispenseTill) {
      const baseAmount = selectedDispenseTill.amount;
      if (baseAmount >= 500) {
        return [
          {
            name: 'Prescription Glasses Frame',
            price: Math.floor(baseAmount * 0.4),
            quantity: 1,
          },
          {
            name: 'Premium Lens Pair',
            price: Math.floor(baseAmount * 0.35),
            quantity: 1,
          },
          {
            name: 'Anti-Glare Coating',
            price: Math.floor(baseAmount * 0.15),
            quantity: 1,
          },
          {
            name: 'Blue Light Filter',
            price: Math.floor(baseAmount * 0.1),
            quantity: 1,
          },
        ];
      }
      return [
        {
          name: `Dispense Item #${selectedDispenseTill.dispenseId}`,
          price: baseAmount,
          quantity: 1,
        },
      ];
    }

    return [
      {
        name: `Dispense #${selectedDispenseTill.dispenseId} – ${formatLabel(selectedDispenseTill.status)}`,
        price: selectedDispenseTill.amount,
        quantity: 1,
      },
    ];
  }, [selectedDispenseTill, dispenseRecord, user?.roleId]);

  const itemsSubtotal = useMemo(
    () =>
      orderItems.reduce(
        (s, i) => s + i.price * (i.quantity > 0 ? i.quantity : 0),
        0,
      ),
    [orderItems],
  );

  const tillAmount = selectedDispenseTill ? selectedDispenseTill.amount : 0;

  const displaySubtotal = useMemo(() => {
    if (
      dispenseRecord != null &&
      dispenseRecord.totalPrice != null &&
      dispenseRecord.totalPrice !== ''
    ) {
      const n = Number(dispenseRecord.totalPrice);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
    if (itemsSubtotal > 0) {
      return itemsSubtotal;
    }
    return tillAmount;
  }, [dispenseRecord, itemsSubtotal, tillAmount]);

  /** Sum of voucher discount amounts stored on linked dispense (NHS / promo rows). */
  const voucherSumFromRecord = useMemo(
    () =>
      dispenseRecord
        ? (dispenseRecord.vouchers ?? []).reduce(
            (s: number, v: any) => s + Number(v?.price ?? 0),
            0,
          )
        : 0,
    [dispenseRecord],
  );

  const voucherLine =
    dispenseRecord && voucherSumFromRecord > 0 ? -voucherSumFromRecord : 0;

  const discountLine = useMemo(() => {
    if (dispenseRecord && Number(dispenseRecord.discount) > 0) {
      const totalDisc = Number(dispenseRecord.discount);
      const manualOnly = Math.max(0, totalDisc - voucherSumFromRecord);
      return manualOnly > 0 ? -manualOnly : 0;
    }
    if (!dispenseRecord && user?.roleId === 'patient' && displaySubtotal > 0) {
      return Math.floor(displaySubtotal * -0.05);
    }
    return 0;
  }, [dispenseRecord, user?.roleId, displaySubtotal, voucherSumFromRecord]);

  const payments = selectedDispenseTill?.payments || [];
  const totalPaid = payments.reduce(
    (sum: number, p: any) => sum + Number(p?.amount ?? 0),
    0,
  );

  const isRefunded = selectedDispenseTill?.status === 'refunded';
  const refundRecorded = isRefunded
    ? Number(selectedDispenseTill?.refundAmount ?? 0)
    : 0;

  const amountPaid = isRefunded
    ? 0
    : totalPaid > 0
      ? totalPaid
      : selectedDispenseTill?.status === 'paid'
        ? selectedDispenseTill?.amount || 0
        : 0;

  const orderTotalAmount = tillAmount;
  const outstandingBalance = isRefunded ? 0 : orderTotalAmount - amountPaid;

  const orderSummary: SummaryItem[] = [
    { label: 'Subtotal:', value: displaySubtotal },
    // The receipt shows prices only. The net / VAT / exempt split lives in the
    // Sales pages (Dispense → Sales), which own sales VAT reporting.
    ...(voucherLine !== 0 ? [{ label: 'Voucher:', value: voucherLine }] : []),
    ...(discountLine !== 0
      ? [{ label: 'Discount:', value: discountLine }]
      : []),
  ];

  const orderTotals: SummaryItem[] = isRefunded
    ? [
        { label: 'Total Amount:', value: orderTotalAmount },
        ...(refundRecorded > 0
          ? [{ label: 'Refunded:', value: refundRecorded }]
          : []),
        { label: 'Outstanding Balance:', value: 0 },
      ]
    : [
        { label: 'Total Amount:', value: orderTotalAmount },
        ...(amountPaid > 0
          ? [{ label: 'Amount Paid:', value: amountPaid }]
          : []),
        { label: 'Outstanding Balance:', value: outstandingBalance },
      ];

  return (
    <Box
      className="print-receipt"
      sx={(theme) => ({
        border: selectedDispenseTill
          ? selectedDispenseTill.status === 'pending' ||
            selectedDispenseTill.status === 'partial'
            ? `2px solid ${theme.palette.warning.main}`
            : selectedDispenseTill.status === 'refunded'
              ? `2px solid ${theme.palette.divider}`
              : `2px solid ${theme.palette.success.main}`
          : `2px solid ${theme.palette.grey[500]}`,
        borderRadius: '8px',
        padding: 4,
        maxWidth: '500px',
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        width: '35%',
        fontFamily: 'Public Sans',
      })}
    >
      {/* Dispense Till Info Section for Print */}
      {selectedDispenseTill && (
        <Box className="dispense-info">
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Till ID: {tillApiData?.till?.id} | Dispense ID: #
            {selectedDispenseTill.dispenseId}
          </Typography>
          <Typography variant="caption">
            Status: {selectedDispenseTill.status.toUpperCase()} |
            {(() => {
              const t = getDispenseTillDisplayInstant(selectedDispenseTill);
              if (selectedDispenseTill.status === 'refunded' && t) {
                return ` Refunded: ${moment(t).format('DD/MM/YYYY HH:mm')}`;
              }
              if (t && selectedDispenseTill.status !== 'refunded') {
                return ` Paid: ${moment(t).format('DD/MM/YYYY HH:mm')}`;
              }
              if (
                selectedDispenseTill.status === 'pending' ||
                selectedDispenseTill.status === 'partial'
              ) {
                return ' Payment Pending';
              }
              return '';
            })()}
          </Typography>
          {selectedDispenseTill.paymentMethod && (
            <Typography variant="caption">
              <br />
              Payment method: {formatLabel(selectedDispenseTill.paymentMethod)}
            </Typography>
          )}
          {receiptRef ? (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Receipt no.: {receiptRef}
            </Typography>
          ) : null}
        </Box>
      )}

      <Typography variant="h6" fontWeight="bold">
        {selectedDispenseTill
          ? `Dispense Order #${selectedDispenseTill.dispenseId}`
          : 'Order Summary'}
      </Typography>

      {/* Order Status Indicator */}
      {selectedDispenseTill && (
        <Box
          sx={(theme) => ({
            mb: 2,
            p: 1,
            borderRadius: 1,
            bgcolor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.neutral
                : selectedDispenseTill.status === 'pending' ||
                    selectedDispenseTill.status === 'partial'
                  ? '#fff3e0'
                  : selectedDispenseTill.status === 'refunded'
                    ? theme.palette.grey[200]
                    : '#e8f5e8',
          })}
        >
          <Typography
            variant="body2"
            sx={(theme) => ({
              fontWeight: 'bold',
              textAlign: 'center',
              color:
                selectedDispenseTill.status === 'pending' ||
                selectedDispenseTill.status === 'partial'
                  ? theme.palette.warning.main
                  : selectedDispenseTill.status === 'refunded'
                    ? theme.palette.text.secondary
                    : theme.palette.success.main,
            })}
          >
            {selectedDispenseTill.status === 'pending'
              ? 'Payment Pending'
              : selectedDispenseTill.status === 'partial'
                ? 'Partially Paid'
                : selectedDispenseTill.status === 'refunded'
                  ? 'Refunded'
                  : 'Payment Completed'}
          </Typography>
        </Box>
      )}

      <Box sx={{ marginTop: 1 }}>
        {orderItems.map((item) => (
          <Box
            key={`${item.name}|${item.price}|${item.category ?? ''}`}
            className="order-item"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              py: 0.75,
              '&:not(:last-of-type)': {
                borderBottom: '1px dashed',
                borderColor: 'divider',
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.quantity > 1 && (
                  <Box
                    component="span"
                    sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.75 }}
                  >
                    {item.quantity} ×
                  </Box>
                )}
                {item.name}
              </Typography>
              {(item.category || item.quantity > 1) && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {[
                    item.category,
                    item.quantity > 1
                      ? `${formatReceiptMoney(item.price)} each`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
            >
              {formatReceiptMoney(
                item.price * (item.quantity > 0 ? item.quantity : 1),
              )}
            </Typography>
          </Box>
        ))}
      </Box>

      {orderSummary.length > 0 && (
        <Box
          className="summary-section"
          sx={(theme) => ({
            marginTop: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            paddingTop: 1,
          })}
        >
          {orderSummary.map((summary, index) => {
            const isVatLine =
              summary.label.includes('Net (excl. VAT)') ||
              summary.label.includes('VAT:');
            return (
              <Typography
                key={index}
                className="order-item"
                sx={{
                  ...typographyStyles,
                  color: isVatLine ? 'text.secondary' : 'text.primary',
                  fontSize: isVatLine ? '0.78rem' : undefined,
                  fontStyle: isVatLine ? 'italic' : undefined,
                  '& span': {
                    color: isVatLine ? 'text.secondary' : 'text.primary',
                  },
                }}
              >
                <span>{summary.label.trim()}</span>
                <span>{formatReceiptMoney(summary.value)}</span>
              </Typography>
            );
          })}
        </Box>
      )}

      <Box
        className="total-section"
        sx={{ marginTop: 2, borderTop: '1px solid #ccc', paddingTop: 1 }}
      >
        {orderTotals.map((total, index) => (
          <Typography key={index} className="order-item" sx={typographyStyles}>
            <Box
              component="span"
              sx={(theme) => ({
                fontWeight: 500,
                color: theme.palette.text.primary,
              })}
            >
              {total.label}
            </Box>

            <Box
              component="span"
              sx={(theme) => ({
                fontWeight:
                  total.label === 'Outstanding Balance:' ? 'bold' : 'normal',
                color:
                  total.label === 'Outstanding Balance:' && total.value > 0
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
              })}
            >
              {formatReceiptMoney(Number(total.value))}
            </Box>
          </Typography>
        ))}
      </Box>

      {/* Wallet Balance Info */}
      {tillApiData && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            bgcolor: 'background.neutral',
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              fontSize: '12px',
              color: 'text.primary',
            }}
          >
            Wallet Balance: £{tillApiData.till.wallet}
          </Typography>
        </Box>
      )}

      {/* Payment Status Message */}
      {selectedDispenseTill && (
        <Box sx={{ mt: 2 }}>
          {selectedDispenseTill.status === 'pending' ? (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: '#f57c00',
                fontStyle: 'italic',
                fontSize: '12px',
              }}
            >
              This order requires payment to complete
            </Typography>
          ) : selectedDispenseTill.status === 'partial' ? (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: '#f57c00',
                fontStyle: 'italic',
                fontSize: '12px',
              }}
            >
              Part of this order has been paid; further payment is required
            </Typography>
          ) : selectedDispenseTill.status === 'refunded' ? (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontStyle: 'italic',
                fontSize: '12px',
              }}
            >
              {selectedDispenseTill.refundMode === 'wallet'
                ? 'Refund credited to wallet'
                : 'This payment has been refunded'}
              {(() => {
                const t = getDispenseTillDisplayInstant(selectedDispenseTill);
                return t ? ` on ${moment(t).format('DD/MM/YYYY')}` : '';
              })()}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: '#388e3c',
                fontStyle: 'italic',
                fontSize: '12px',
              }}
            >
              Payment completed{' '}
              {selectedDispenseTill.paymentDate
                ? `on ${moment(selectedDispenseTill.paymentDate).format(
                    'DD/MM/YYYY',
                  )}`
                : ''}
            </Typography>
          )}
        </Box>
      )}

      <Box
        className="no-print"
        sx={{ mt: 16, display: 'flex', alignItems: 'center' }}
      >
        {/* <Typography
          sx={{ fontWeight: "100", fontSize: "12px", color: "black" }}>
          Send Receipt on Email
        </Typography> */}
        {/* <Checkbox defaultChecked color="default" size="small" /> */}
      </Box>

      <Box
        className="no-print"
        sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}
      >
        <Button
          variant="outlined"
          sx={{
            ...buttonStyles,
            opacity: selectedDispenseTill ? 1 : 0.5,
          }}
          onClick={() => window.print()}
          disabled={!selectedDispenseTill}
        >
          Print Receipt
        </Button>
      </Box>
    </Box>
  );
};

export default OrderSummary;
